/**
 * Governance P3 API Client
 *
 * Handles all P3 governance hardening and P3 standards API calls:
 * - Consequence Tier Gating
 * - Earned Authority Lifecycle
 * - FI-to-Authority Wiring
 * - Intent Capsule HMAC
 * - Invariant Promotion Pipeline
 * - Load-Bearing Classification
 *
 * Copyright (c) 2026 Equilateral AI. All rights reserved.
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

export type { TimComboAPIResponse };

// =============================================================================
// CONSEQUENCE TIER GATING — Types
// =============================================================================

export interface TierGateDecision {
  Gate_Decision_ID: string;
  Agent_Name: string;
  Requested_Tier: string;
  Effective_Tier: string;
  Verdict: 'PASS' | 'HOLD' | 'DENY' | 'ESCALATE';
  Reason: string;
  FI_Score_At_Evaluation: number;
  Drift_Detected: boolean;
  Evaluated_At?: string;
}

export interface TierGateConfig {
  Tier: string;
  Default_Verdict: string;
  FI_Threshold: number;
  Require_Approval: boolean;
}

// =============================================================================
// EARNED AUTHORITY LIFECYCLE — Types
// =============================================================================

export interface AuthorityLifecycleEvent {
  Event_ID: string;
  Agent_Name: string;
  Previous_Maturity: string;
  New_Maturity: string;
  Trigger_Type: string;
  Triggered_At: string;
}

// =============================================================================
// FI-TO-AUTHORITY WIRING — Types
// =============================================================================

export interface FIAuthorityMapping {
  Agent_Name: string;
  FI_Score: number;
  FI_Status: 'NORMAL' | 'WARNING' | 'DOWNGRADED' | 'CRITICAL';
  Effective_Ceiling: string;
}

// =============================================================================
// INTENT CAPSULE HMAC — Types
// =============================================================================

export interface IntentCapsule {
  Capsule_ID: string;
  Mandate: string;
  Keywords: string[];
  HMAC_Signature: string;
  Drift_Detected: boolean;
  Drift_Score: number;
  Expires_At: string;
}

// =============================================================================
// INVARIANT PROMOTION — Types
// =============================================================================

export interface InvariantCandidate {
  Candidate_ID: string;
  Rule_Text: string;
  Domain: string;
  Promotion_Score: number;
  Occurrence_Count: number;
  Tests: {
    universality: boolean;
    consequence: boolean;
    context: boolean;
  };
}

export interface PromotedInvariant {
  Invariant_ID: string;
  Rule_Text: string;
  Domain: string;
  Enforcement_Level: string;
  Tier: string;
  Violation_Count: number;
}

// =============================================================================
// LOAD-BEARING CLASSIFICATION — Types
// =============================================================================

export interface LoadBearingElement {
  Element_ID: string;
  Element_Type: string;
  Element_Name: string;
  Classification: 'load_bearing' | 'supplementary' | 'decorative';
  Failure_Impact_Score: number;
}

// =============================================================================
// CONSEQUENCE TIER GATING — API Functions
// =============================================================================

export async function evaluateTierGate(
  clientId: string,
  data: { Agent_Name: string; Requested_Tier: string; Context?: Record<string, unknown> }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.TIER_GATE_EVALUATE,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

export async function getTierGateHistory(
  clientId: string,
  opts?: { limit?: number; offset?: number; Agent_Name?: string }
): Promise<TimComboAPIResponse> {
  const params: Record<string, string> = { Client_ID: clientId };
  if (opts?.limit) params.limit = opts.limit.toString();
  if (opts?.offset) params.offset = opts.offset.toString();
  if (opts?.Agent_Name) params.Agent_Name = opts.Agent_Name;

  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.TIER_GATE_HISTORY,
    'GET',
    null,
    { params }
  );
}

export async function getTierGateConfig(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.TIER_GATE_CONFIG_GET,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}

export async function updateTierGateConfig(
  clientId: string,
  data: { Tier: string; Default_Verdict?: string; FI_Threshold?: number; Require_Approval?: boolean }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.TIER_GATE_CONFIG_PUT,
    'PUT',
    { Client_ID: clientId, ...data }
  );
}

// =============================================================================
// EARNED AUTHORITY LIFECYCLE — API Functions
// =============================================================================

export async function getAuthorityLifecycle(
  clientId: string,
  opts?: { Agent_Name?: string; limit?: number }
): Promise<TimComboAPIResponse> {
  const params: Record<string, string> = { Client_ID: clientId };
  if (opts?.Agent_Name) params.Agent_Name = opts.Agent_Name;
  if (opts?.limit) params.limit = opts.limit.toString();

  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.AUTHORITY_LIFECYCLE,
    'GET',
    null,
    { params }
  );
}

export async function getPromotionHistory(
  clientId: string,
  opts?: { Agent_Name?: string; limit?: number }
): Promise<TimComboAPIResponse> {
  const params: Record<string, string> = { Client_ID: clientId };
  if (opts?.Agent_Name) params.Agent_Name = opts.Agent_Name;
  if (opts?.limit) params.limit = opts.limit.toString();

  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.AUTHORITY_PROMOTION_HISTORY,
    'GET',
    null,
    { params }
  );
}

export async function triggerDemotion(
  clientId: string,
  data: { Agent_Name: string; Reason: string }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.AUTHORITY_DEMOTION_TRIGGER,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

// =============================================================================
// FI-TO-AUTHORITY WIRING — API Functions
// =============================================================================

export async function getFIAuthorityMapping(
  clientId: string,
  opts?: { Agent_Name?: string }
): Promise<TimComboAPIResponse> {
  const params: Record<string, string> = { Client_ID: clientId };
  if (opts?.Agent_Name) params.Agent_Name = opts.Agent_Name;

  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.FI_AUTHORITY_MAPPING,
    'GET',
    null,
    { params }
  );
}

export async function getFIDowngradeHistory(
  clientId: string,
  opts?: { Agent_Name?: string; limit?: number }
): Promise<TimComboAPIResponse> {
  const params: Record<string, string> = { Client_ID: clientId };
  if (opts?.Agent_Name) params.Agent_Name = opts.Agent_Name;
  if (opts?.limit) params.limit = opts.limit.toString();

  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.FI_DOWNGRADE_HISTORY,
    'GET',
    null,
    { params }
  );
}

export async function updateFIThresholds(
  clientId: string,
  data: { Warning_Threshold?: number; Downgrade_Threshold?: number; Critical_Threshold?: number }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.FI_THRESHOLDS_CONFIG,
    'PUT',
    { Client_ID: clientId, ...data }
  );
}

// =============================================================================
// INTENT CAPSULE HMAC — API Functions
// =============================================================================

export async function signCapsule(
  clientId: string,
  data: { Mandate: string; Keywords: string[]; TTL_Minutes?: number }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.CAPSULE_SIGN,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

export async function verifyCapsule(
  clientId: string,
  data: { Capsule_ID: string; HMAC_Signature: string }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.CAPSULE_VERIFY,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

export async function getCapsuleDriftCheck(
  clientId: string,
  opts?: { Capsule_ID?: string; limit?: number }
): Promise<TimComboAPIResponse> {
  const params: Record<string, string> = { Client_ID: clientId };
  if (opts?.Capsule_ID) params.Capsule_ID = opts.Capsule_ID;
  if (opts?.limit) params.limit = opts.limit.toString();

  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_GOVERNANCE.CAPSULE_DRIFT_CHECK,
    'GET',
    null,
    { params }
  );
}

// =============================================================================
// INVARIANT PROMOTION PIPELINE — API Functions
// =============================================================================

export async function getInvariantCandidates(
  clientId: string,
  opts?: { Domain?: string; Min_Score?: number }
): Promise<TimComboAPIResponse> {
  const params: Record<string, string> = { Client_ID: clientId };
  if (opts?.Domain) params.Domain = opts.Domain;
  if (opts?.Min_Score) params.Min_Score = opts.Min_Score.toString();

  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_STANDARDS.INVARIANT_CANDIDATES,
    'GET',
    null,
    { params }
  );
}

export async function promoteInvariant(
  clientId: string,
  data: { Candidate_ID: string; Enforcement_Level: string; Tier: string }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_STANDARDS.INVARIANT_PROMOTE,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

export async function getInvariantHistory(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_STANDARDS.INVARIANT_HISTORY,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}

// =============================================================================
// LOAD-BEARING CLASSIFICATION — API Functions
// =============================================================================

export async function getLoadBearingElements(
  clientId: string,
  opts?: { Classification?: string; Element_Type?: string }
): Promise<TimComboAPIResponse> {
  const params: Record<string, string> = { Client_ID: clientId };
  if (opts?.Classification) params.Classification = opts.Classification;
  if (opts?.Element_Type) params.Element_Type = opts.Element_Type;

  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_STANDARDS.LOAD_BEARING_ELEMENTS,
    'GET',
    null,
    { params }
  );
}

export async function runLoadBearingAnalysis(
  clientId: string,
  data: { Element_Types?: string[]; Force_Refresh?: boolean }
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_STANDARDS.LOAD_BEARING_ANALYZE,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

export async function getLoadBearingHistory(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.P3_STANDARDS.LOAD_BEARING_HISTORY,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}
